import { httpsCallable } from 'firebase/functions';

import { functions } from '@/firebase-config.ts';
import { logger } from '@/utils/logger.ts';

import {
  decodeActionItemsManagementPayload,
  type ActionItemsClient,
  type ActionItemsManagementPayload,
  type ActionItemsManagementSnapshot,
  type SubscribeActionItemsManagementRequest,
} from '../contracts/action-items-client.ts';
import type { Unsubscribe } from '../contracts/subscription.ts';
import type { ProjectScope } from '@/features/sync/project-scope.ts';

const INITIAL_RECONNECT_DELAY_MS = 250;
const MAX_RECONNECT_DELAY_MS = 5000;
const STREAM_DEADLINE_MS = 65_000;
const TERMINAL_ERROR_CODES = new Set([
  'functions/unauthenticated',
  'functions/permission-denied',
  'functions/resource-exhausted',
  'functions/invalid-argument',
]);
const ACCESS_DENIAL_ERROR_CODES = new Set([
  'functions/unauthenticated',
  'functions/permission-denied',
]);

type StreamRequest = ProjectScope & { includeReviewedEvidenceKeys: boolean };
type StreamResponse = { complete: true };
type StreamResult = {
  stream: AsyncIterable<unknown>;
  data: Promise<StreamResponse>;
};

function cloneSnapshot(snapshot: ActionItemsManagementSnapshot): ActionItemsManagementSnapshot {
  return {
    status: snapshot.status,
    items: [...snapshot.items],
    events: [...snapshot.events],
    reviewedEvidenceKeys: [...snapshot.reviewedEvidenceKeys],
  };
}

function errorCode(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null || !('code' in error)) return;
  return typeof error.code === 'string' ? error.code : undefined;
}

async function consumeStreamResult(
  result: StreamResult,
  controller: AbortController,
  onChunk: (chunk: unknown) => void,
): Promise<unknown> {
  const dataOutcome = result.data.then(
    () => ({ status: 'fulfilled' as const }),
    (error: unknown) => ({ status: 'rejected' as const, error }),
  );
  let primaryError: unknown;
  try {
    for await (const chunk of result.stream) onChunk(chunk);
  } catch (cause) {
    primaryError = cause;
    controller.abort();
  }
  const finalOutcome = await dataOutcome;
  if (primaryError !== undefined) return primaryError;
  return finalOutcome.status === 'rejected' ? finalOutcome.error : undefined;
}

export class FirebaseActionItemsClient implements ActionItemsClient {
  subscribeManagement(
    scope: ProjectScope,
    request: SubscribeActionItemsManagementRequest,
    onUpdate: (snapshot: ActionItemsManagementSnapshot) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    const callable = httpsCallable<StreamRequest, StreamResponse, unknown>(
      functions,
      'subscribeActionItemsManagement',
    );
    let snapshot: ActionItemsManagementSnapshot = {
      status: 'loading', items: [], events: [], reviewedEvidenceKeys: [],
    };
    let active = true;
    let reconnectDelayMs = INITIAL_RECONNECT_DELAY_MS;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
    let deadlineTimer: ReturnType<typeof setTimeout> | undefined;
    let controller: AbortController | undefined;

    const emit = (status: ActionItemsManagementSnapshot['status'], payload?: ActionItemsManagementPayload) => {
      snapshot = payload
        ? { status, items: payload.items, events: payload.events, reviewedEvidenceKeys: payload.reviewedEvidenceKeys }
        : { ...snapshot, status };
      onUpdate(cloneSnapshot(snapshot));
    };

    const scheduleReconnect = () => {
      if (!active) return;
      emit('reconnecting');
      const delay = reconnectDelayMs;
      reconnectDelayMs = Math.min(reconnectDelayMs * 2, MAX_RECONNECT_DELAY_MS);
      reconnectTimer = setTimeout(() => { void connect(); }, delay);
    };

    const connect = async (): Promise<void> => {
      controller = new AbortController();
      const invocationController = controller;
      let deadlineExpired = false;
      let primaryError: unknown;
      deadlineTimer = setTimeout(() => {
        deadlineExpired = true;
        invocationController.abort();
      }, STREAM_DEADLINE_MS);
      try {
        const result = await callable.stream({
          ...scope,
          includeReviewedEvidenceKeys: request.access === 'edit',
        }, { signal: invocationController.signal });
        primaryError = await consumeStreamResult(
          result,
          invocationController,
          chunk => {
            if (!active) return;
            const payload = decodeActionItemsManagementPayload(chunk);
            reconnectDelayMs = INITIAL_RECONNECT_DELAY_MS;
            emit('ready', payload);
          },
        );
      } catch (cause) {
        primaryError = cause;
      } finally {
        if (deadlineTimer !== undefined) clearTimeout(deadlineTimer);
        deadlineTimer = undefined;
      }

      if (!active) return;
      if (deadlineExpired) {
        const error = new Error('Action Items management stream exceeded its local deadline');
        logger.error('[FirebaseActionItemsClient] Management stream error:', error);
        onError?.(error);
        scheduleReconnect();
        return;
      }
      if (primaryError !== undefined) {
        const error = primaryError instanceof Error
          ? primaryError
          : new Error('Action Items management stream failed');
        logger.error('[FirebaseActionItemsClient] Management stream error:', error);
        onError?.(error);
        const code = errorCode(primaryError) ?? '';
        if (TERMINAL_ERROR_CODES.has(code)) {
          if (ACCESS_DENIAL_ERROR_CODES.has(code)) {
            emit('error', { items: [], events: [], reviewedEvidenceKeys: [] });
          } else {
            emit('error');
          }
        } else {
          scheduleReconnect();
        }
        return;
      }
      scheduleReconnect();
    };

    emit('loading');
    void connect();

    return () => {
      active = false;
      if (reconnectTimer !== undefined) clearTimeout(reconnectTimer);
      if (deadlineTimer !== undefined) clearTimeout(deadlineTimer);
      controller?.abort();
    };
  }
}
